import { useMemo, useState } from 'react';
import { COMMIT_COUNTS } from './data';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseDate(iso: string): Date {
  return new Date(iso + 'T12:00:00');
}

function toIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** 0 = Mon … 6 = Sun (ISO week ordering) */
function isoWeekday(date: Date): number {
  return (date.getDay() + 6) % 7;
}

function intensityLevel(count: number): 0 | 1 | 2 | 3 | 4 {
  if (count === 0) return 0;
  if (count <= 3) return 1;
  if (count <= 7) return 2;
  if (count <= 13) return 3;
  return 4;
}

const LEVEL_CLASSES: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: 'bg-muted',
  1: 'bg-foreground/20',
  2: 'bg-foreground/40',
  3: 'bg-foreground/65',
  4: 'bg-foreground/90',
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ---------------------------------------------------------------------------
// Build the grid
// ---------------------------------------------------------------------------

interface GridCell {
  iso: string;
  count: number;
  level: 0 | 1 | 2 | 3 | 4;
  /** whether this cell is outside the real data range (padding) */
  filler: boolean;
}

function buildGrid(commitCounts: Record<string, number>): {
  weeks: GridCell[][];
  monthLabels: { label: string; colIndex: number }[];
} {
  const dates = Object.keys(commitCounts).sort();
  const first = parseDate(dates[0]);
  const last = parseDate(dates[dates.length - 1]);

  // Pad backwards to Monday of first week
  const startDow = isoWeekday(first); // 0=Mon
  const gridStart = addDays(first, -startDow);

  // Pad forwards to Sunday of last week
  const endDow = isoWeekday(last); // 0=Mon, 6=Sun
  const gridEnd = addDays(last, 6 - endDow);

  const totalDays =
    Math.round((gridEnd.getTime() - gridStart.getTime()) / 86400000) + 1;
  const totalWeeks = Math.ceil(totalDays / 7);

  const weeks: GridCell[][] = [];
  const monthLabels: { label: string; colIndex: number }[] = [];
  let seenMonths = new Set<string>();

  for (let w = 0; w < totalWeeks; w++) {
    const week: GridCell[] = [];
    for (let d = 0; d < 7; d++) {
      const date = addDays(gridStart, w * 7 + d);
      const iso = toIso(date);
      const count = commitCounts[iso] ?? 0;
      const filler = date < first || date > last;

      week.push({ iso, count, level: intensityLevel(count), filler });

      // Track month labels on the first row (Monday)
      if (d === 0) {
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        if (!seenMonths.has(monthKey)) {
          seenMonths.add(monthKey);
          monthLabels.push({
            label: date.toLocaleDateString('en-US', { month: 'short' }),
            colIndex: w,
          });
        }
      }
    }
    weeks.push(week);
  }

  return { weeks, monthLabels };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ActivityGraph() {
  const { weeks, monthLabels } = useMemo(
    () => buildGrid(COMMIT_COUNTS),
    []
  );

  const [tooltip, setTooltip] = useState<{
    iso: string;
    count: number;
    x: number;
    y: number;
  } | null>(null);

  const CELL = 11; // px — cell size
  const GAP = 2;   // px — gap between cells
  const STEP = CELL + GAP;

  return (
    <div className="relative select-none">
      {/* Month labels */}
      <div className="mb-1 flex pl-8" style={{ gap: 0 }}>
        {monthLabels.map(({ label, colIndex }, i) => {
          // Position each label at its column
          const nextCol =
            i + 1 < monthLabels.length
              ? monthLabels[i + 1].colIndex
              : weeks.length;
          const widthCols = nextCol - colIndex;
          return (
            <span
              key={`${label}-${colIndex}`}
              className="text-[10px] text-muted-foreground"
              style={{ width: widthCols * STEP, flexShrink: 0 }}
            >
              {label}
            </span>
          );
        })}
      </div>

      <div className="flex gap-1">
        {/* Day-of-week labels */}
        <div
          className="flex flex-col justify-between pr-1"
          style={{ gap: GAP, paddingTop: 1 }}
        >
          {DAY_LABELS.map((d, i) => (
            <span
              key={d}
              className="text-[9px] leading-none text-muted-foreground"
              style={{ height: CELL, lineHeight: `${CELL}px`, opacity: i % 2 === 0 ? 1 : 0 }}
            >
              {d}
            </span>
          ))}
        </div>

        {/* Heatmap grid */}
        <div className="flex" style={{ gap: GAP }}>
          {weeks.map((week, wi) => (
            <div key={wi} className="flex flex-col" style={{ gap: GAP }}>
              {week.map((cell) => (
                <div
                  key={cell.iso}
                  className={[
                    'rounded-[2px] transition-opacity',
                    cell.filler
                      ? 'bg-muted opacity-20'
                      : LEVEL_CLASSES[cell.level],
                    !cell.filler && cell.count > 0
                      ? 'cursor-pointer hover:ring-1 hover:ring-foreground/40'
                      : '',
                  ].join(' ')}
                  style={{ width: CELL, height: CELL, flexShrink: 0 }}
                  onMouseEnter={(e) => {
                    if (cell.filler) return;
                    const rect = (e.target as HTMLElement).getBoundingClientRect();
                    setTooltip({ iso: cell.iso, count: cell.count, x: rect.left, y: rect.top });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-2 flex items-center gap-1.5 pl-8">
        <span className="text-[10px] text-muted-foreground">Less</span>
        {([0, 1, 2, 3, 4] as const).map((l) => (
          <div
            key={l}
            className={`rounded-[2px] ${LEVEL_CLASSES[l]}`}
            style={{ width: CELL, height: CELL }}
          />
        ))}
        <span className="text-[10px] text-muted-foreground">More</span>
      </div>

      {/* Floating tooltip */}
      {tooltip && (
        <div
          className="pointer-events-none fixed z-50 rounded border border-border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-md"
          style={{ left: tooltip.x + 16, top: tooltip.y - 8 }}
        >
          <span className="font-medium">{tooltip.count} commit{tooltip.count !== 1 ? 's' : ''}</span>
          {' · '}
          {new Date(tooltip.iso + 'T12:00:00').toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </div>
      )}
    </div>
  );
}
